import React, { useEffect, useMemo, useRef } from 'react';
import { EmptyStateIcons, Table, TableColumn } from '@silverfort/sf-components';
import InsightColumn from './columns/InsightColumn';
import TotalColumn from './columns/TotalColumn';
import useGetRefSize from '../../../hooks/useGetRefSize';
import { useIspmContext } from '../IspmContext';
import { getRiskIndicatorByRiskRule, RISK_CATEGORIES } from '@silverfort-nodelibs/common/utils/insight';
import { getRiskIcon, RISKS_FOR_INSIGHTS_TABLE } from '../../../consts/Insight';
import { Statistics, StatisticsDiff } from '../api/types/Insights';
import SeverityColumn from './columns/SeverityColumn';
import { compareDisabledExposures, compareDesc, compareAsc } from './InsightsTableUtil';

const TABLE_HEADER_HEIGHT = 56;

const SORT_COLUMN_TOTAL_ENTITIES = 'totalEntities';
const SORT_COLUMN_SEVERITY = 'severity';

export interface InsightRow {
    id: string;
    title: string;
    categoryTitle?: string;
    categoryKey?: string;
    totalEntities: number;
    entitiesDiff?: number;
    resolvedEntities: number;
    resolvedEntitiesDiff?: number;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    severity: number;
    isCustom: boolean;
    isHidden: boolean
}

const shouldShowRiskIndicator = (
    insight: string,
    count: number,
    customInsightsIdsToNames: Record<string, string>,
    showEmptyAndHiddenExposures: boolean,
    hiddenRiskIndicators: Set<string>
): boolean => {
    const isValidInsight =  RISKS_FOR_INSIGHTS_TABLE.has(insight) || insight in customInsightsIdsToNames;
    if (!isValidInsight) return false

    // Show all insights
    if (showEmptyAndHiddenExposures) return true

    const isHidden = hiddenRiskIndicators.has(insight)
    const hasActiveExposures = count > 0
    return !isHidden && hasActiveExposures
}

const getInsightsRows = (
    insightsStatistics: Statistics = {},
    insightsStatisticsDiff: { [key: string]: StatisticsDiff } = {},
    resolvedInsightsStatistics: Statistics = {},
    resolvedInsightsStatisticsDiff: { [key: string]: StatisticsDiff } = {},
    customInsightsIdsToNames: Record<string, string>,
    sortColumn: string,
    sortDirection: string,
    showEmptyAndHiddenExposures: boolean,
    hiddenRiskIndicators: string[] | undefined
): InsightRow[] => {
    const defaultSortColumn = showEmptyAndHiddenExposures ? SORT_COLUMN_TOTAL_ENTITIES : SORT_COLUMN_SEVERITY;
    const defaultSortDirection = 'desc';
    
    const isDefaultSort = sortColumn === defaultSortColumn && sortDirection === defaultSortDirection;
    
    const hiddenRiskIndicatorsSet = new Set(hiddenRiskIndicators ?? []);

    
    
    return Object.entries(insightsStatistics ?? {})
        .filter(([key, value]) => shouldShowRiskIndicator(
            key,
            value,
            customInsightsIdsToNames,
            showEmptyAndHiddenExposures,
            hiddenRiskIndicatorsSet)
        )
        .map(([key, value]) => {
            const riskIndicator = getRiskIndicatorByRiskRule(key);
            return {
                id: key,
                title: riskIndicator?.insightsTitle ?? customInsightsIdsToNames[key] ?? key,
                totalEntities: value,
                entitiesDiff: insightsStatisticsDiff[key]?.added - insightsStatisticsDiff[key]?.removed,
                resolvedEntities: resolvedInsightsStatistics[key] ?? 0,
                resolvedEntitiesDiff:
                    resolvedInsightsStatisticsDiff[key]?.added - resolvedInsightsStatisticsDiff[key]?.removed,
                categoryTitle: riskIndicator?.category?.title || RISK_CATEGORIES.CUSTOM_INSIGHTS.title,
                categoryKey: riskIndicator?.category?.key || RISK_CATEGORIES.CUSTOM_INSIGHTS.key,
                icon: getRiskIcon(key),
                severity: riskIndicator?.severity ? riskIndicator.severity : 0,
                isCustom: key in customInsightsIdsToNames,
                isHidden: hiddenRiskIndicatorsSet.has(key)
            };
        })
        .sort((a, b) => {            
            if (isDefaultSort) {
                const primaryColumn = defaultSortColumn;
                const secondaryColumn = showEmptyAndHiddenExposures ? SORT_COLUMN_SEVERITY : SORT_COLUMN_TOTAL_ENTITIES;

                const disabledComparison = compareDisabledExposures(a, b, showEmptyAndHiddenExposures);
                if (disabledComparison !== null) return disabledComparison;

                if (a[primaryColumn] === b[primaryColumn]) {
                    if (a[secondaryColumn] === b[secondaryColumn]) {
                        return 0;
                    }
                    return compareDesc(a[secondaryColumn], b[secondaryColumn]);
                }
                return compareDesc(a[primaryColumn], b[primaryColumn]);
            } else {
                const disabledComparison = compareDisabledExposures(a, b, showEmptyAndHiddenExposures);
                if (disabledComparison !== null) return disabledComparison;

                if (a[sortColumn] === b[sortColumn]) {
                    const secondaryColumn = sortColumn === SORT_COLUMN_SEVERITY ? SORT_COLUMN_TOTAL_ENTITIES : SORT_COLUMN_SEVERITY;
                    if (a[secondaryColumn] === b[secondaryColumn]) {
                        return 0;
                    }
                    return compareDesc(a[secondaryColumn], b[secondaryColumn]);
                }

                if (sortDirection === 'asc') {
                    return compareAsc(a[sortColumn], b[sortColumn]);
                } else {
                    return compareDesc(a[sortColumn], b[sortColumn]);
                }
            }
        });
};

const InsightsTable = () => {
    const {
        insightsData,
        setSelectedInsight,
        categories,
        severitiesFilter,
        selectedInsight,
        searchedExposureName,
        isLoadingPageData,
        customInsightsIdsToNames,
        showEmptyAndHiddenExposures
    } = useIspmContext();
    const {
        insightsStatistics,
        insightsStatisticsDiff,
        resolvedInsightsStatistics,
        resolvedInsightsStatisticsDiff,
        customInsights,
        hiddenRiskIndicators,
    } = insightsData ?? {};

    const ref = useRef(null);
    const { height } = useGetRefSize(ref);

    const getDefaultSortColumn = (showEmpty: boolean) => 
        showEmpty ? SORT_COLUMN_TOTAL_ENTITIES : SORT_COLUMN_SEVERITY;
    
    const [sortColumn, setSortColumn] = React.useState(() => getDefaultSortColumn(showEmptyAndHiddenExposures));
    const [sortDirection, setSortDirection] = React.useState('desc');
    const [highlightedRow, setHighlightedRow] = React.useState<string[]>([]);

    useEffect(() => {
        const defaultSortColumn = getDefaultSortColumn(showEmptyAndHiddenExposures);
    
        const previousDefault = getDefaultSortColumn(!showEmptyAndHiddenExposures);
        if (sortColumn === previousDefault && sortDirection === 'desc') {
            setSortColumn(defaultSortColumn);
        }
    }, [showEmptyAndHiddenExposures]);

    const rows = useMemo(
        () =>
            getInsightsRows(
                insightsStatistics,
                insightsStatisticsDiff,
                resolvedInsightsStatistics,
                resolvedInsightsStatisticsDiff,
                customInsightsIdsToNames,
                sortColumn,
                sortDirection,
                showEmptyAndHiddenExposures,
                hiddenRiskIndicators,
            ),
        [
            customInsights,
            insightsStatistics,
            insightsStatisticsDiff,
            resolvedInsightsStatistics,
            resolvedInsightsStatisticsDiff,
            customInsightsIdsToNames,
            sortColumn,
            sortDirection,
            showEmptyAndHiddenExposures,
            hiddenRiskIndicators
        ]
    );

    useEffect(() => {
        if (selectedInsight) {
            setHighlightedRow([selectedInsight]);
        } else {
            setHighlightedRow([]);
        }
    }, [selectedInsight]);

    useEffect(() => {
        if (!insightsData) {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const rule = params.get('rule');
        if (rule && (RISKS_FOR_INSIGHTS_TABLE.has(rule) || rule in customInsightsIdsToNames)) {
            setSelectedInsight(rule);
        }
    }, [insightsData]);

    const handleSort = (columnKey: string, sortDirection: string) => {
        setSortColumn(columnKey);
        setSortDirection(sortDirection);
    };

    const filteredRows = useMemo(
        () =>
            rows.filter((row) => {
                return (
                    (categories?.includes(row.categoryKey ?? '') || !categories?.length) &&
                    (severitiesFilter?.includes(row.severity) || !severitiesFilter?.length) &&
                    (row.title.toLowerCase().includes(searchedExposureName?.toLowerCase() ?? '') ||
                        !searchedExposureName)
                );
            }),
        [categories, rows, searchedExposureName, severitiesFilter]
    );

    const columns: TableColumn<InsightRow>[] = useMemo(
        () => [
            {
                columnKey: 'title',
                title: `Exposures (${filteredRows.length})`,
                render: (row) => <InsightColumn icon={row.icon} title={row.title} isBlurred={row.isHidden} />,
                columnClassName: 'flex items-center max-w-[40%] h-full font-sf'
            },
            {
                columnKey: 'severity',
                title: `Severity`,
                render: (row) => <SeverityColumn severity={row.severity} isBlurred={row.isHidden || row.totalEntities === 0} />,
                columnClassName: 'flex items-center max-w-12 h-full font-sf'
            },
            {
                columnKey: 'totalEntities',
                title: 'Entities',
                render: (row) => <TotalColumn
                    total={row.isHidden ? '-': row.totalEntities}
                    diff={row.isHidden ? undefined: row.entitiesDiff}
                    isBlurred={row.isHidden}
                />,
                columnClassName: 'flex items-center max-w-[20%] h-full font-sf'
            },
            {
                columnKey: 'resolvedEntities',
                title: 'Resolved entities',
                render: (row) => (
                    <TotalColumn
                        total={row.isHidden ? '-' : row.resolvedEntities}
                        diff={row.isHidden ? undefined: row.resolvedEntitiesDiff}
                        isBlurred={row.isHidden || row.totalEntities === 0}
                    />
                ),
                columnClassName: 'flex items-center max-w-[20%] h-full font-sf'
            },
            {
                columnKey: 'categoryTitle',
                title: 'Type',
                render: (row) => <div className={'flex flex-1 flex-row items-center justify-center gap-2 text-13' + (row.isHidden ? ' opacity-20' : '')}>
                    <span className='block flex-1 truncate'>{row.categoryTitle}</span>
                </div>
            }
        ],
        [filteredRows]
    );

    const onRowClick = (row: InsightRow) => {
        setSelectedInsight(row.id);
    };

    return (
        <div className="flex w-full h-full" ref={ref}>
            <Table
                loading={isLoadingPageData}
                columns={columns}
                rows={filteredRows}
                rowClassName={`rounded-lg mb-4 border ${isLoadingPageData ? 'bg-sf-gray-200 animate-pulse' : ''}`}
                rowHeight={64}
                bodyHeight={(height ?? 0) - TABLE_HEADER_HEIGHT}
                tableContainerClassName="h-full w-full"
                onSortChange={handleSort}
                EmptyStateProps={{
                    className: 'w-full h-max',
                    title: 'No Results',
                    description: 'There are no results matching to your search',
                    icon: EmptyStateIcons.NoResults
                }}
                keyExtractor={(row) => row.id}
                rowMargin={8}
                onRowClick={onRowClick}
                highlighted={highlightedRow}
            />
        </div>
    );
};

export default InsightsTable;
